import { queryCreateObject } from 'app/backend/Common'
import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router'

interface MissingObject {
  type: string
}

interface Props {
  missingObjects: MissingObject[]
  formId: string
}

/**
 * React observer component responsible for detecting missing Salesforce-related objects
 * in a form and sending a single warning log to the backend when an invalid state is detected.
 *
 * The component does not render any UI and exists purely for side-effect tracking.
 *
 * It uses an internal ref to prevent duplicate log submissions and resets that flag
 * when the form is no longer in a "broken" state (i.e. no missing objects are present).
 *
 * The component is only rendered when a valid `formId` is available, ensuring that
 * logs always include proper context for debugging and analytics purposes.
 *
 * @category Form
 * @subcategory Form observers
 * @component
 * @returns {null}
 *
 * @param {Object[]} missingObjects Array of missing objects detected in the form state.
 * @param {string} formId Unique identifier of the form instance used for logging context.
 * @param {Object} queryCreateObject.params Parameters for log creation.
 * @param {string} queryCreateObject.params.type Type of log entry (e.g. "Error Log").
 * @param {Object} queryCreateObject.params.createData Payload describing the error event.
 * @param {string} queryCreateObject.params.createData.severity Severity level of the log (e.g. "warning").
 * @param {string} queryCreateObject.params.createData.description Short human-readable description of the issue.
 * @param {string} queryCreateObject.params.createData.longDescription JSON string containing detailed context
 * (formId, missingObjects, and pathname).
 */
export const FormMissingObjectsObserver = ({
  missingObjects,
  formId
}: Props): null => {
  const hasLoggedRef = useRef(false)
  const location = useLocation()
  const pathname = location?.pathname

  useEffect(() => {
    if (pathname.length === 0) return

    const hasMissingObjects = missingObjects.length > 0

    // reset flag when form is not broken anymore
    if (!hasMissingObjects) {
      hasLoggedRef.current = false
      return
    }

    // prevent duplicate logs
    if (hasLoggedRef.current) return

    hasLoggedRef.current = true

    void queryCreateObject({
      type: 'Error Log',
      createData: {
        severity: 'warning',
        description: 'Missing object id in form',
        longDescription: JSON.stringify({
          formId,
          missingObjects,
          path: pathname
        })
      }
    })
  }, [missingObjects, pathname, formId])

  return null
}
